import toast from 'react-hot-toast';

/**
 * Files and folders to ignore when copying project structure
 * These are typically generated/vendor files that shouldn't be shared
 */
const IGNORED_FILES = [
  'node_modules',
  '.git',
  '.env',
  'dist',
  'build',
  '.next',
  '.DS_Store',
  'package-lock.json',
  'yarn.lock',
  '.gitignore',
  '.env.local',
  '.env.*.local',
  'npm-debug.log',
  'yarn-error.log',
];

/**
 * Check if a file/folder should be ignored
 * @param {string} name - The name of the file or folder
 * @param {string} path - The full path to the file or folder
 * @returns {boolean} - True if should be ignored
 */
export const shouldIgnoreFile = (name, path) => {
  // Check direct name match
  if (IGNORED_FILES.includes(name)) {
    return true;
  }

  // Check if any part of the path contains ignored directories
  const pathParts = path.split('/');
  for (const part of pathParts) {
    if (IGNORED_FILES.includes(part)) {
      return true;
    }
  }

  // Check for wildcard patterns like .env.*
  for (const ignored of IGNORED_FILES) {
    if (ignored.includes('*')) {
      const pattern = ignored.replace('*', '.*');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(name)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Recursively build a formatted string of the entire project structure
 * Including folder hierarchy and file contents (filters ignored files)
 * @param {object} node - The file tree node
 * @param {string} indent - Current indentation level
 * @returns {string} - Formatted project structure
 */
export const buildProjectStructure = (node, indent = '') => {
  if (!node) return '';

  let result = '';

  if (node.type === 'folder') {
    // Skip root folder or ignored folders
    if (indent && node.name !== 'root' && !shouldIgnoreFile(node.name, node.path)) {
      result += `${indent}${node.name}/\n`;
      indent += '  ';
    }

    // Add filtered children
    if (node.children) {
      node.children.forEach(child => {
        // Skip ignored files/folders
        if (!shouldIgnoreFile(child.name, child.path)) {
          result += buildProjectStructure(child, indent === '' ? '  ' : indent);
        }
      });
    }
  } else if (node.type === 'file') {
    // Skip ignored files
    if (!shouldIgnoreFile(node.name, node.path)) {
      const fileSize = node.size || 0;
      const maxSize = 200 * 1024; // 200KB limit

      if (fileSize > maxSize) {
        result += `${indent}${node.name}\n${indent}  (File too large: ${Math.round(fileSize / 1024)}KB)\n\n`;
      } else {
        result += `${indent}${node.name}\n`;
        if (
          node.content &&
          node.content !== '// File too large to display inline. Download to view.' &&
          node.content !== '// Binary file - cannot display'
        ) {
          result += `${indent}${'─'.repeat(40)}\n`;
          const lines = node.content.split('\n').slice(0, 500); // Limit to first 500 lines
          result += lines.map(line => `${indent}${line}`).join('\n');
          if (node.content.split('\n').length > 500) {
            result += `\n${indent}... (${node.content.split('\n').length - 500} more lines)\n`;
          }
          result += `\n${indent}${'─'.repeat(40)}\n\n`;
        } else {
          result += `${indent}  (Binary or unreadable file)\n\n`;
        }
      }
    }
  }

  return result;
};

/**
 * Build complete project output including metadata
 * @param {object} project - The project object
 * @returns {string} - Complete formatted output
 */
const buildCompleteProjectOutput = (project) => {
  let projectText = `PROJECT: ${project.name}\nGenerated: ${new Date().toLocaleString()}\n\n`;
  projectText += `${'═'.repeat(60)}\n\n`;

  project.fileTree.children.forEach(child => {
    projectText += buildProjectStructure(child, '');
  });

  return projectText;
};

/**
 * Build full ZIP output (raw mode) - includes all files as a continuous block
 * @param {object} project - The project object
 * @returns {string} - Raw file output
 */
export const buildFullZipOutput = (project) => {
  let output = `FULL PROJECT DUMP: ${project.name}\nGenerated: ${new Date().toLocaleString()}\n\n`;
  output += `${'═'.repeat(60)}\n\n`;

  let fileCount = 0;

  const traverseFiles = (node, currentPath = '') => {
    if (!node) return;

    if (node.type === 'folder') {
      if (node.children) {
        node.children.forEach(child => {
          const newPath = currentPath ? `${currentPath}/${child.name}` : child.name;
          if (!shouldIgnoreFile(child.name, newPath)) {
            traverseFiles(child, newPath);
          }
        });
      }
    } else if (node.type === 'file') {
      const filePath = currentPath ? `${currentPath}/${node.name}` : node.name;
      if (!shouldIgnoreFile(node.name, filePath)) {
        fileCount++;
        output += `\n${'─'.repeat(60)}\n`;
        output += `FILE: ${filePath}\n`;
        output += `Size: ${node.size ? Math.round(node.size / 1024) + 'KB' : 'Unknown'}\n`;
        output += `Language: ${node.language || 'plaintext'}\n`;
        output += `${'─'.repeat(60)}\n\n`;

        if (
          node.content &&
          node.content !== '// File too large to display inline. Download to view.' &&
          node.content !== '// Binary file - cannot display'
        ) {
          output += node.content;
        } else {
          output += '(Binary or unreadable file)';
        }
        output += '\n\n';
      }
    }
  };

  traverseFiles(project.fileTree);

  output += `\n${'═'.repeat(60)}\n`;
  output += `Total files: ${fileCount}\n`;
  output += `Generated: ${new Date().toLocaleString()}\n`;

  return output;
};

/**
 * Build a single file output for copying individual files
 * @param {object} fileNode - The file node
 * @returns {string} - File content with metadata
 */
export const buildSingleFileOutput = (fileNode) => {
  if (!fileNode || fileNode.type !== 'file') {
    return '';
  }

  let output = `FILE: ${fileNode.path}\n`;
  output += `Language: ${fileNode.language || 'plaintext'}\n`;
  output += `Size: ${fileNode.size ? Math.round(fileNode.size / 1024) + 'KB' : 'Unknown'}\n`;
  output += `${'═'.repeat(60)}\n\n`;

  if (
    fileNode.content &&
    fileNode.content !== '// File too large to display inline. Download to view.' &&
    fileNode.content !== '// Binary file - cannot display'
  ) {
    output += fileNode.content;
  } else {
    output += '(Binary or unreadable file)';
  }

  return output;
};

/**
 * Copy text to clipboard with fallback
 * @param {string} text - Text to copy
 * @param {string} successMessage - Success message to show
 * @returns {Promise<boolean>} - True if successful
 */
const copyToClipboard = async (text, successMessage = 'Copied to clipboard!') => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
    return true;
  } catch (err) {
    console.error('Clipboard API error:', err);
    // Fallback for clipboard API errors
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(successMessage);
      return true;
    } catch {
      toast.error('Failed to copy. Try using your browser\'s developer tools.');
      return false;
    }
  }
};

/**
 * Copy entire project structure to clipboard
 * @param {object} project - The project object
 * @returns {Promise<boolean>} - True if successful
 */
export const copyProjectToClipboard = async (project) => {
  try {
    if (!project?.fileTree?.children || project.fileTree.children.length === 0) {
      toast.error('No files to copy');
      return false;
    }

    const projectText = buildCompleteProjectOutput(project);
    return await copyToClipboard(projectText, 'Project copied to clipboard! 🚀');
  } catch (err) {
    console.error('Copy project error:', err);
    toast.error('Failed to copy project');
    return false;
  }
};

/**
 * Copy full ZIP (raw mode) to clipboard
 * @param {object} project - The project object
 * @returns {Promise<boolean>} - True if successful
 */
export const copyFullZipToClipboard = async (project) => {
  try {
    if (!project?.fileTree?.children || project.fileTree.children.length === 0) {
      toast.error('No files to copy');
      return false;
    }

    const zipText = buildFullZipOutput(project);
    return await copyToClipboard(zipText, 'Full ZIP copied to clipboard! 📦');
  } catch (err) {
    console.error('Copy full ZIP error:', err);
    toast.error('Failed to copy full ZIP');
    return false;
  }
};

/**
 * Copy a single file to clipboard
 * @param {object} fileNode - The file node to copy
 * @returns {Promise<boolean>} - True if successful
 */
export const copySingleFileToClipboard = async (fileNode) => {
  try {
    if (!fileNode || fileNode.type !== 'file') {
      toast.error('Please select a file');
      return false;
    }

    if (!fileNode.content) {
      toast.error('Cannot copy this file (binary or too large)');
      return false;
    }

    const fileText = buildSingleFileOutput(fileNode);
    return await copyToClipboard(fileText, `${fileNode.name} copied!`);
  } catch (err) {
    console.error('Copy file error:', err);
    toast.error('Failed to copy file');
    return false;
  }
};
